import type { Alumni, Donation, Event, Mentorship, Newsletter, Registration, Survey } from "../../services/alumniService";
import { byNumber, byText, reversed, type RegisterConfig } from "../register/registerLogic";

/**
 * Alumni relations: the directory of former pupils, reunions and other events,
 * giving, mentorships of current learners, newsletters and surveys. The actions
 * here mirror the server's (mark_paid, check_in, acknowledge, transition).
 */

export const day = (value?: string | null) => (value ? value.slice(0, 10) : "");
export const ugx = (value?: string | number | null) => `UGX ${Math.round(Number(value ?? 0)).toLocaleString()}`;
export const time = (value?: string | null) => (value ? value.slice(0, 5) : "");

type Pairs = [string, string][];
export const EVENT_TYPES: Pairs = [["reunion", "Reunion"], ["networking", "Networking"], ["fundraising", "Fundraising"], ["workshop", "Workshop"], ["seminar", "Seminar"], ["social", "Social"], ["other", "Other"]];
export const DONATION_TYPES: Pairs = [["monetary", "Money"], ["equipment", "Equipment"], ["books", "Books"], ["time", "Volunteer time"], ["other", "Other"]];
export const MENTORSHIP_TYPES: Pairs = [["career", "Career guidance"], ["academic", "Academic support"], ["life_skills", "Life skills"], ["entrepreneurship", "Entrepreneurship"], ["other", "Other"]];
export const SURVEY_TYPES: Pairs = [["career", "Career"], ["satisfaction", "Satisfaction"], ["feedback", "Feedback"], ["event", "Event"], ["other", "Other"]];
export const labelOf = (pairs: Pairs, value?: string | null) => pairs.find(([key]) => key === value)?.[1] ?? (value || "—");

type Tone = { label: string; dot: string };

export function eventState(row: Pick<Event, "is_active" | "is_published" | "event_date" | "registration_deadline" | "max_attendees" | "registered_count">, today: string): Tone {
  if (!row.is_active) return { label: "Cancelled", dot: "bg-gray-400" };
  if (!row.is_published) return { label: "Draft", dot: "bg-gray-400" };
  if (row.event_date < today) return { label: "Past", dot: "bg-sky-500" };
  if (row.max_attendees && (row.registered_count ?? 0) >= row.max_attendees) return { label: "Full", dot: "bg-amber-500" };
  if (row.registration_deadline && row.registration_deadline < today) return { label: "Registration closed", dot: "bg-amber-500" };
  return { label: "Open", dot: "bg-emerald-500" };
}

/** Whether an alumnus can still sign up, and why not. */
export function canRegister(row: Event, today: string) {
  if (row.my_registration) return { ok: false, reason: "You are registered." };
  const state = eventState(row, today).label;
  return state === "Open" ? { ok: true, reason: "" } : { ok: false, reason: `Registration isn't open: ${state.toLowerCase()}.` };
}

export const paymentLabel = (status: Registration["payment_status"]) => ({ pending: "Not paid", paid: "Paid", free: "Free" })[status] ?? status;

export const MENTORSHIP_STATUS: Record<Mentorship["status"], Tone> = {
  active: { label: "Active", dot: "bg-emerald-500" },
  on_hold: { label: "On hold", dot: "bg-amber-500" },
  completed: { label: "Completed", dot: "bg-sky-500" },
  cancelled: { label: "Cancelled", dot: "bg-gray-400" },
};

/** Where a mentorship can go from here. */
export function mentorshipMoves(status: Mentorship["status"]) {
  const moves: Record<Mentorship["status"], { value: Mentorship["status"]; label: string }[]> = {
    active: [{ value: "on_hold", label: "Put on hold" }, { value: "completed", label: "Mark completed" }, { value: "cancelled", label: "Cancel" }],
    on_hold: [{ value: "active", label: "Resume" }, { value: "completed", label: "Mark completed" }, { value: "cancelled", label: "Cancel" }],
    completed: [],
    cancelled: [],
  };
  return moves[status];
}

export function surveyState(row: Pick<Survey, "is_active" | "start_date" | "end_date">, today: string): Tone {
  if (!row.is_active) return { label: "Closed", dot: "bg-gray-400" };
  if (row.start_date > today) return { label: "Not open yet", dot: "bg-gray-400" };
  if (row.end_date < today) return { label: "Ended", dot: "bg-sky-500" };
  return { label: "Open", dot: "bg-emerald-500" };
}

/** The survey's questions from a textarea: one per line, blanks dropped. */
export const questionsFrom = (text: string) => text.split("\n").map((line) => line.trim()).filter(Boolean);

// ---- Registers ---------------------------------------------------------------

export const alumniConfig: RegisterConfig<Alumni> = {
  search: (row) => [row.user_name, row.student_id, row.graduation_class, row.current_company, row.job_title, row.current_occupation, row.industry, row.email, row.phone_number].filter(Boolean).join(" "),
  views: [
    { key: "active", label: "Active", match: (row) => row.is_active },
    { key: "donors", label: "Donors", match: (row) => row.is_donor },
    { key: "volunteers", label: "Volunteers", match: (row) => row.is_volunteer },
    { key: "no_contact", label: "No phone or email", match: (row) => !row.phone_number && !row.email },
    { key: "all", label: "All" },
  ],
  category: { label: "Class of", value: (row) => String(row.graduation_year) },
  sorts: [
    { key: "name", label: "Name A–Z", compare: byText((row) => row.user_name) },
    { key: "recent", label: "Most recent class", compare: reversed(byNumber((row) => row.graduation_year)) },
    { key: "oldest", label: "Oldest class", compare: byNumber((row) => row.graduation_year) },
    { key: "giving", label: "Most given", compare: reversed(byNumber((row) => row.donation_total)) },
  ],
};

export function eventConfig(today: string): RegisterConfig<Event> {
  return {
    search: (row) => `${row.name} ${row.venue ?? ""} ${labelOf(EVENT_TYPES, row.event_type)}`,
    views: [
      { key: "upcoming", label: "Upcoming", match: (row) => row.is_active && row.event_date >= today },
      { key: "drafts", label: "Drafts", match: (row) => row.is_active && !row.is_published },
      { key: "past", label: "Past", match: (row) => row.event_date < today },
      { key: "all", label: "All" },
    ],
    date: { label: "Date", value: (row) => row.event_date },
    category: { label: "Kind", value: (row) => labelOf(EVENT_TYPES, row.event_type) },
    sorts: [
      { key: "soonest", label: "Soonest first", compare: byText((row) => `${row.event_date}${row.start_time}`) },
      { key: "latest", label: "Latest first", compare: reversed(byText((row) => `${row.event_date}${row.start_time}`)) },
      { key: "popular", label: "Most registered", compare: reversed(byNumber((row) => row.registered_count)) },
    ],
  };
}

export const donationConfig: RegisterConfig<Donation> = {
  search: (row) => `${row.alumni_name} ${row.description} ${row.purpose ?? ""} ${row.fund ?? ""}`,
  views: [
    { key: "all", label: "All" },
    { key: "unthanked", label: "Not yet thanked", match: (row) => !row.acknowledgment_sent },
    { key: "money", label: "Money", match: (row) => row.donation_type === "monetary" },
    { key: "kind", label: "In kind", match: (row) => row.donation_type !== "monetary" },
  ],
  date: { label: "Given", value: (row) => row.donation_date },
  category: { label: "Fund", value: (row) => row.fund },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.donation_date)) },
    { key: "largest", label: "Largest first", compare: reversed(byNumber((row) => row.amount)) },
  ],
};

export const mentorshipConfig: RegisterConfig<Mentorship> = {
  search: (row) => `${row.mentor_name} ${row.mentee_name} ${row.title}`,
  views: [
    { key: "active", label: "Active", match: (row) => row.status === "active" },
    { key: "on_hold", label: "On hold", match: (row) => row.status === "on_hold" },
    { key: "ended", label: "Completed or cancelled", match: (row) => row.status === "completed" || row.status === "cancelled" },
    { key: "all", label: "All" },
  ],
  date: { label: "Started", value: (row) => row.start_date },
  category: { label: "Kind", value: (row) => labelOf(MENTORSHIP_TYPES, row.mentorship_type) },
  sorts: [
    { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.start_date)) },
    { key: "mentor", label: "Mentor A–Z", compare: byText((row) => row.mentor_name) },
  ],
};

export const newsletterConfig: RegisterConfig<Newsletter> = {
  search: (row) => `${row.title} ${row.content} ${row.tags.join(" ")}`,
  views: [
    { key: "all", label: "All" },
    { key: "published", label: "Published", match: (row) => row.is_published },
    { key: "drafts", label: "Drafts", match: (row) => !row.is_published },
  ],
  date: { label: "Published", value: (row) => row.publication_date },
  sorts: [{ key: "newest", label: "Newest first", compare: reversed(byText((row) => row.publication_date)) }],
};

export function surveyConfig(today: string): RegisterConfig<Survey> {
  return {
    search: (row) => `${row.title} ${row.description ?? ""} ${row.questions.join(" ")}`,
    views: [
      { key: "open", label: "Open", match: (row) => surveyState(row, today).label === "Open" },
      { key: "ended", label: "Ended or closed", match: (row) => ["Ended", "Closed"].includes(surveyState(row, today).label) },
      { key: "all", label: "All" },
    ],
    date: { label: "Opens", value: (row) => row.start_date },
    sorts: [
      { key: "newest", label: "Newest first", compare: reversed(byText((row) => row.start_date)) },
      { key: "answers", label: "Most answers", compare: reversed(byNumber((row) => row.response_count)) },
    ],
  };
}
